import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AutoComplete } from "antd";

const DEBOUNCE_MS = 200;
const MAX_RESULTS = 100;

const INPUT_CLS =
  "h-[42px] px-3.5 focus:outline-none block w-full text-sm border border-gray-200 rounded-xl text-gray-800 bg-white transition-colors focus:border-yellow-400 focus:ring-2 focus:ring-yellow-100 hover:border-gray-300 disabled:bg-gray-50 disabled:text-gray-400 disabled:border-gray-100 disabled:cursor-not-allowed disabled:hover:border-gray-100";

// Client-side filtered port field backed by the preloaded list_ocean_port
// master list (see index.js's memoized `portList`) — used for POL, Inland CY
// Origin/Destination, POD and the Routing Legs Origin/Destination Port
// fields. Production's port list runs into the thousands of rows (vs. a
// handful locally), so unlike a plain antd `filterOption`, which re-scans
// the full array synchronously on every keystroke, this debounces the scan
// and caps rendered matches to MAX_RESULTS so typing stays responsive
// regardless of list size.
//
// The filter query is debounced off `value` itself (the field's single
// source of truth) rather than a separately-tracked `onSearch` string —
// tracking search text independently left the filter stuck showing the
// unfiltered default list whenever `value` was already populated without a
// matching onSearch event (e.g. an existing saved quote's prefilled port, or
// right after picking an option), since that separate state never learned
// what the box actually displayed.
const PortAutoComplete = ({ name, value, options, setFieldValue, placeholder, disabled, inputClassName }) => {
  const [debouncedValue, setDebouncedValue] = useState(value || "");
  const debounceRef = useRef(null);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedValue(value || ""), DEBOUNCE_MS);
    return () => clearTimeout(debounceRef.current);
  }, [value]);

  const handleChange = useCallback((val) => setFieldValue(name, val), [setFieldValue, name]);

  const filteredOptions = useMemo(() => {
    const list = options || [];
    const query = debouncedValue.trim().toLowerCase();
    if (!query) return list.slice(0, MAX_RESULTS);
    const matches = [];
    for (const option of list) {
      if ((option?.label || "").toLowerCase().includes(query)) {
        matches.push(option);
        if (matches.length >= MAX_RESULTS) break;
      }
    }
    return matches;
  }, [options, debouncedValue]);

  return (
    <AutoComplete
      value={value || ""}
      options={filteredOptions}
      filterOption={false}
      onChange={handleChange}
      onSelect={handleChange}
      disabled={disabled}
      style={{ width: "100%" }}
      popupMatchSelectWidth={false}
    >
      <input type="text" placeholder={placeholder} className={inputClassName || INPUT_CLS} />
    </AutoComplete>
  );
};

export default PortAutoComplete;
